import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Form, Input, Button, Card, Typography, Alert, ConfigProvider, Spin } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import AuthService from '../../services/authService';
import { getAdminApiBaseUrl } from '../../utils/adminApiBase';
import './Login.css';

const { Title, Text } = Typography;

const ADMIN_API_BASE = getAdminApiBaseUrl();

const Login = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [bootstrapping, setBootstrapping] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;

        async function verifyStoredSession() {
            if (!AuthService.isUsableToken()) {
                setBootstrapping(false);
                return;
            }
            const token = AuthService.getToken();
            try {
                await axios.get(`${ADMIN_API_BASE}/settings/menus`, {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 15000,
                });
                if (!cancelled) navigate('/');
            } catch {
                if (!cancelled) {
                    AuthService.logout({ redirect: false });
                }
            } finally {
                if (!cancelled) setBootstrapping(false);
            }
        }

        verifyStoredSession();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const onFinish = async values => {
        const { adminId, password } = values;
        setError('');
        setLoading(true);

        try {
            const response = await AuthService.login(adminId, password);
            if (response) {
                navigate('/');
            } else {
                setError('로그인에 실패했습니다.');
            }
        } catch (err) {
            setError(err.message || '로그인 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    if (bootstrapping) {
        return (
            <div className="admin-login-page" style={{ minHeight: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#1e3a5f',
                    borderRadius: 8,
                },
            }}
        >
            <div className="admin-login-page">
                <Card
                    bordered={false}
                    style={{
                        width: '100%',
                        maxWidth: 420,
                        boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
                        borderRadius: 16,
                    }}
                    bodyStyle={{ padding: '40px 36px' }}
                >
                    <div style={{ textAlign: 'center', marginBottom: 36 }}>
                        <SafetyCertificateOutlined
                            style={{
                                fontSize: 40,
                                color: '#1e3a5f',
                                marginBottom: 12,
                            }}
                        />
                        <Title
                            level={2}
                            style={{
                                color: '#1e3a5f',
                                fontWeight: 800,
                                margin: 0,
                                letterSpacing: '-0.5px',
                            }}
                        >
                            ADCARE 관리자
                        </Title>
                        <Text type="secondary">운영·회원·한도·로그 관리 콘솔</Text>
                    </div>

                    {error && (
                        <Alert
                            message={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 24 }}
                        />
                    )}

                    <Form
                        name="admin_login"
                        onFinish={onFinish}
                        layout="vertical"
                        size="large"
                        requiredMark={false}
                    >
                        <Form.Item
                            label="관리자 아이디"
                            name="adminId"
                            rules={[{ required: true, message: '아이디를 입력해주세요.' }]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                placeholder="아이디"
                                autoComplete="username"
                            />
                        </Form.Item>

                        <Form.Item
                            label="비밀번호"
                            name="password"
                            rules={[{ required: true, message: '비밀번호를 입력해주세요.' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                placeholder="비밀번호"
                                autoComplete="current-password"
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                loading={loading}
                                className="admin-login-submit"
                                icon={<SafetyCertificateOutlined />}
                            >
                                {loading ? '로그인 처리 중...' : '로그인'}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export { Login };
